import React, { useEffect, useState } from 'react';
import { 
  MdSecurity, MdLock, MdVisibility, MdDownload, MdEdit, MdWarning,
  MdCheckCircle, MdEmail, MdPhone, MdLocationOn, MdAccessTime,
  MdPerson, MdCreditCard, MdCookie, MdAnalytics, MdSettings,
  MdContactSupport, MdPrivacyTip, MdPolicy, MdNote, MdStar,
  MdLanguage, MdShare, MdCloud, MdStorage, MdVpnLock,
  MdVerifiedUser, MdGavel, MdAssignment, MdFeedback, MdHelp,
  MdQuestionAnswer, MdKeyboardArrowRight, MdExpandMore,
  MdArrowDownward, MdArrowUpward, MdMenu, MdClose, MdDelete
} from 'react-icons/md';
import { FaUserShield, FaShieldAlt, FaRegHandshake } from 'react-icons/fa';
import { RiShieldCheckLine, RiTeamLine } from 'react-icons/ri';
import '../style.css';

const PrivacyPolicy = () => {
  const [lastUpdated, setLastUpdated] = useState('');
  const [activeSection, setActiveSection] = useState('overview');
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    setLastUpdated(new Date().toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }));
  }, []);

  const sections = [
    { id: 'overview', title: 'Overview', icon: <MdPolicy />, color: '#4F46E5' },
    { id: 'collection', title: 'Data Collection', icon: <MdAnalytics />, color: '#10B981' },
    { id: 'usage', title: 'Data Usage', icon: <MdSettings />, color: '#F59E0B' },
    { id: 'protection', title: 'Data Protection', icon: <MdSecurity />, color: '#EF4444' },
    { id: 'cookies', title: 'Cookies Policy', icon: <MdCookie />, color: '#8B5CF6' },
    { id: 'rights', title: 'Your Rights', icon: <FaUserShield />, color: '#06B6D4' },
    { id: 'thirdparty', title: 'Third Parties', icon: <RiTeamLine />, color: '#EC4899' },
    { id: 'contact', title: 'Contact Us', icon: <MdContactSupport />, color: '#0891B2' }
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    setShowMobileMenu(false);
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="privacy-container">
      {/* Mobile Menu Button */}
      <div className="mobile-nav">
        <button 
          className="menu-btn"
          onClick={() => setShowMobileMenu(!showMobileMenu)}
        >
          {showMobileMenu ? <MdClose /> : <MdMenu />}
          <span>Menu</span>
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {showMobileMenu && (
        <div className="mobile-overlay" onClick={() => setShowMobileMenu(false)}>
          <div className="mobile-menu" onClick={e => e.stopPropagation()}>
            <div className="menu-header">
              <h3><MdPolicy /> Sections</h3>
              <button className="close-btn" onClick={() => setShowMobileMenu(false)}>
                <MdClose />
              </button>
            </div>
            <div className="menu-items">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`menu-link ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(section.id)}
                >
                  <span className="link-icon">{section.icon}</span>
                  <span>{section.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <header className="privacy-hero">
        <div className="hero-content">
          <div className="hero-icon">
            <MdPrivacyTip className="main-icon" />
          </div>
          <h1 className="hero-title">
            <MdPolicy /> Privacy Policy
          </h1>
          <p className="hero-desc">
            Your trust is our priority. We protect your information with highest security standards.
          </p>
          
          <div className="hero-tags">
            <div className="tag-item">
              <MdVerifiedUser />
              <span>GDPR Compliant</span>
            </div>
            <div className="tag-item">
              <MdAccessTime />
              <span>Updated: {lastUpdated}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="content-wrapper">
        {/* Desktop Sidebar */}
        <aside className="privacy-sidebar">
          <div className="sidebar-content">
            <div className="sidebar-header">
              <h3><MdPolicy /> Quick Links</h3>
            </div>
            
            <nav className="sidebar-nav">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`nav-btn ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(section.id)}
                >
                  <div className="btn-icon">
                    {section.icon}
                  </div>
                  <span>{section.title}</span>
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Policy Content */}
        <main className="privacy-content">
          
          {/* Overview Section */}
          <section id="overview" className="policy-card">
            <div className="card-header">
              <div className="header-icon">
                <MdPolicy />
              </div>
              <h2>Privacy Policy Overview</h2>
            </div>
            
            <div className="card-body">
              <div className="welcome-note">
                <MdStar />
                <p>Welcome to Varanasi Tour & Travels Privacy Policy.</p>
              </div>
              
              <div className="key-points">
                <h4><MdCheckCircle /> Key Principles</h4>
                <div className="points-grid">
                  <div className="point-item">
                    <MdLock />
                    <h5>Security First</h5>
                    <p>Top security for your data</p>
                  </div>
                  <div className="point-item">
                    <MdVisibility />
                    <h5>Transparency</h5>
                    <p>Clear communication</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Data Collection */}
          <section id="collection" className="policy-card">
            <div className="card-header">
              <div className="header-icon">
                <MdAnalytics />
              </div>
              <h2>Information We Collect</h2>
            </div>
            
            <div className="card-body">
              <div className="data-grid">
                <div className="data-item">
                  <h4><MdPerson /> Personal Info</h4>
                  <ul>
                    <li><MdCheckCircle /> Full name & contact</li>
                    <li><MdCheckCircle /> Date of birth</li>
                  </ul>
                </div>
                
                <div className="data-item">
                  <h4><MdCreditCard /> Booking Info</h4>
                  <ul>
                    <li><MdCheckCircle /> Travel dates</li>
                    <li><MdCheckCircle /> Payment details</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Data Protection */}
          <section id="protection" className="policy-card">
            <div className="card-header">
              <div className="header-icon">
                <MdSecurity />
              </div>
              <h2>Data Security</h2>
            </div>
            
            <div className="card-body">
              <div className="security-grid">
                <div className="security-item">
                  <MdVpnLock />
                  <h4>SSL Encryption</h4>
                  <p>End-to-end encryption</p>
                </div>
                
                <div className="security-item">
                  <MdStorage />
                  <h4>Secure Storage</h4>
                  <p>Certified data centers</p>
                </div>
              </div>
            </div>
          </section>

          {/* Your Rights */}
          <section id="rights" className="policy-card">
            <div className="card-header">
              <div className="header-icon">
                <FaUserShield />
              </div>
              <h2>Your Rights</h2>
            </div>
            
            <div className="card-body">
              <div className="rights-grid">
                <div className="right-item">
                  <MdVisibility />
                  <div>
                    <h4>Right to Access</h4>
                    <p>Request your data copies</p>
                  </div>
                </div>
                
                <div className="right-item">
                  <MdDelete />
                  <div>
                    <h4>Right to Erasure</h4>
                    <p>Request data deletion</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="policy-card">
            <div className="card-header">
              <div className="header-icon">
                <MdContactSupport />
              </div>
              <h2>Contact Us</h2>
            </div>
            
            <div className="card-body">
              <div className="contact-info">
                <h4><MdHelp /> Need Help?</h4>
                
                <div className="contact-grid">
                  <div className="contact-item">
                    <MdEmail />
                    <div>
                      <h5>Email</h5>
                      <p>privacy@varanasitours.com</p>
                    </div>
                  </div>
                  
                  <div className="contact-item">
                    <MdPhone />
                    <div>
                      <h5>Phone</h5>
                      <p>+916307291567
</p>
                    </div>
                  </div>
                </div>
                
                <div className="contact-actions">
                  <button className="primary-btn">
                    <MdContactSupport /> Contact Support
                  </button>
                </div>
              </div>
            </div>
          </section>

        </main>
      </div>

      {/* Footer */}
      <footer className="policy-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <MdPrivacyTip />
            <div>
              <h4>Varanasi Tour & Travels</h4>
              <p>Your Trusted Travel Partner</p>
            </div>
          </div>
          
          <div className="footer-legal">
            <p>Policy effective from {lastUpdated}</p>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default PrivacyPolicy;